import { graphql } from "@apollo/client/react/hoc"
import Alert from "@pathwright/ui/src/components/alert/Alert"
import Button from "@pathwright/ui/src/components/button/Button"
import Card from "@pathwright/ui/src/components/card/Card"
import CardBlock from "@pathwright/ui/src/components/card/CardBlock"
import CardLink from "@pathwright/ui/src/components/card/CardLink"
import Copy from "@pathwright/ui/src/components/copy/Copy"
import RadioButton from "@pathwright/ui/src/components/form/form-radio/RadioButton"
import RadioGroup from "@pathwright/ui/src/components/form/form-radio/RadioGroup"
import FormStateCard from "@pathwright/ui/src/components/form/form-state/FormStateCard"
import FormStateSubmitButton from "@pathwright/ui/src/components/form/form-state/FormStateSubmitButton"
import { FormikWithDiscardFormState } from "@pathwright/ui/src/components/form/form-state/FormikDiscardFormState"
import { getFormStateStorageKey } from "@pathwright/ui/src/components/form/form-state/useFormStateStorage"
import TextInput from "@pathwright/ui/src/components/form/form-text-input/TextInput"
import ToggleInput from "@pathwright/ui/src/components/form/form-toggle/ToggleInput"
import Fieldset from "@pathwright/ui/src/components/form/form-utils/Fieldset"
import {
  getFormError,
  validate
} from "@pathwright/ui/src/components/form/utils"
import CountIndicator from "@pathwright/ui/src/components/indicator/CountIndicator"
import LoadingCircle from "@pathwright/ui/src/components/loading/LoadingCircle"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import Text from "@pathwright/ui/src/components/ui/Text"
import { withTranslatedResourceTypeLabel } from "@pathwright/web/src/modules/resource/utils"
import compose from "lodash/flowRight"
import pick from "lodash/pick"
import path from "path-browserify"
import PropTypes from "prop-types"
import { Component } from "react"
import styled from "styled-components"
// import ReactSelectInput from "@pathwright/ui/src/components/form/form-select/ReactSelectInput"
import { GroupDocument, PathItemDocument } from "../../api/generated"
import SelectInput from "../../lib/SelectInput/SelectInput"
import {
  againstField,
  customErrorMessage,
  findErrorMessage,
  isGreaterThanOrEqualTo,
  isInt,
  isLessThanOrEqualTo,
  validateIf
} from "../../utils/validators"
import {
  ALLOW_RESETS_ALWAYS,
  ALLOW_RESETS_BEFORE,
  ANYONE_CAN_PREVIEW,
  GRADING_TYPE_AUTOMATIC,
  GRADING_TYPE_MANUAL,
  PASSWORD_LOCKED,
  REGISTERED_MEMBERS_ONLY,
  SHOW_GRADING_FEEDBACK_ALWAYS,
  SHOW_GRADING_FEEDBACK_COMPLETION,
  SHOW_GRADING_FEEDBACK_NEVER
} from "../constants"
import withUpdatePathItem from "../graphql/withUpdatePathItem"

window.validators = [isInt, isGreaterThanOrEqualTo(0)]

const StyledCard = styled(FormStateCard)`
  form .UIFieldWrapper__label-wrapper {
    width: auto !important;
    font-weight: 700;
    color: rgba(42, 46, 51, 1);
  }

  /* removing left margin on error messages */
  form .UIFieldWrapper__label-wrapper + .styled-field > span {
    margin-left: 0;
  }

  /* standardize TextInput input widths */
  form .TextInput input {
    max-width: 200px;
    min-width: 3rem;
  }

  .CardLink {
    h5 {
      font-size: 1em;
    }
  }

  form .UIFieldWrapper__label-wrapper,
  form .UIFieldWrapper__label-wrapper h5 {
    margin-bottom: 3px;
  }

  .cursor-pointer,
  .cursor-pointer .Tooltip__trigger.OverlayTrigger,
  .cursor-pointer label {
    cursor: pointer;
  }

  .UIFieldWrapper {
    .Tooltip__trigger {
      margin-top: -0.5em;
    }

    & > p {
      display: grid;
      grid-template-columns: 1fr auto;
    }

    & > div:first-of-type {
      display: grid;
      grid-template-columns: 1fr auto;
    }

    &.SelectInput .UIFieldWrapper__input {
      width: 230px;
    }

    &.has-custom {
      & > div:first-of-type,
      & > p {
        grid-template-columns: 1fr minmax(3rem, min-content) auto;
      }
    }
  }
`

const StyledCustom = styled.div`
  margin-left: 5px;
`

const COMMENT_LABELS_MAP = {
  D: "Don't allow comment",
  O: "Allow comment with completion",
  R: "Require a comment with completion"
}

const HELP_ARTICLE =
  "https://help.pathwright.com/managing-course-settings/step-and-lesson-settings/step-settings-overview-adjust-completion-points-and-visibility"

const tooltipWithHelpArticleLink = (tooltip, article = HELP_ARTICLE) => (
  <span>
    {tooltip}
    <br />
    <br />
    <Button
      styleType="text"
      href={article}
      target="_blank"
      style={{
        whiteSpace: "nowrap"
      }}
      label="Learn More"
      postIcon="chevron-right"
      inverted
    />
  </span>
)

class EditStepSettingsCard extends Component {
  state = {
    error: null
  }

  UNSAFE_componentWillReceiveProps(nextProps, nextState) {
    if (
      nextProps.error &&
      nextProps.error !== this.props.error &&
      nextProps.error !== this.state.error
    ) {
      this.handleError(nextProps.error.message)
    }
  }

  handleError = (error) => {
    this.setState({ error })
  }

  get hasLegacyContent() {
    return (
      this.props.pathItem.content_type !== "blocks" &&
      this.props.pathItem.content_type !== null
    )
  }

  filterValues(values, moreSettings) {
    moreSettings =
      moreSettings !== undefined ? moreSettings : this.props.displayMoreSettings

    return moreSettings
      ? pick(values, [
          "step_visibility",
          "lock_password",
          "is_previewable",
          "allow_resets",
          "show_grading_feedback",
          "requires_api_completion",
          "completion_comment_required_status"
        ])
      : pick(values, [
          "time_to_complete",
          "is_required",
          "grading_type",
          "points",
          "minimum_passing_score"
        ])
  }

  get initialValue() {
    // Implement Season 2, 2021 "Make Reviewable" changes
    const convertGradingTypeToMakeReviewable = () => {
      const { grading_type } = this.props.pathItem

      // Manual grading for our purposes is Make Reviewable === true
      return grading_type === GRADING_TYPE_MANUAL
    }

    // Implement Season 2, 2021 "Step visibility" changes
    const getVisibilitySetting = () => {
      const { lock_password, is_previewable } = this.props.pathItem

      return !!lock_password
        ? PASSWORD_LOCKED
        : is_previewable
        ? ANYONE_CAN_PREVIEW
        : REGISTERED_MEMBERS_ONLY
    }

    return {
      is_required: this.props.pathItem.is_required,
      allow_resets: this.props.pathItem.allow_resets === ALLOW_RESETS_ALWAYS,
      show_grading_feedback: this.props.pathItem.show_grading_feedback,
      completion_comment_required_status:
        this.props.pathItem.completion_comment_required_status,
      points: this.props.pathItem.points || 0,
      minimum_passing_score: this.props.pathItem.minimum_passing_score || 0,
      grading_type: convertGradingTypeToMakeReviewable(),
      is_previewable: this.props.pathItem.is_previewable,
      lock_password: this.props.pathItem.lock_password,
      time_to_complete: !isNaN(parseInt(this.props.pathItem.time_to_complete))
        ? this.props.pathItem.time_to_complete / 60
        : null,
      requires_api_completion:
        !!this.props.pathItem.completion.requires_api_completion,
      step_visibility: getVisibilitySetting()
    }
  }

  onSubmit = async ({ step_visibility, ...data }) => {
    // Implement Season 2, 2021 "Make Reviewable" changes
    const convertMakeReviewableToGradingType = () => {
      // For our purposes, "Make Reviewable" === "M" (manual) grading type
      return data.grading_type ? GRADING_TYPE_MANUAL : GRADING_TYPE_AUTOMATIC
    }

    // Implement Season 2, 2021 "Step visibility" changes
    const isLocked = step_visibility === PASSWORD_LOCKED
    //   * if for some reason a step is both locked and previewable, disable previewable now
    const isPreviewable = step_visibility === ANYONE_CAN_PREVIEW
    const applyStepVisibiity = () => {
      return {
        is_previewable: !isLocked && isPreviewable
      }
    }

    try {
      const allValues = {
        ...data,
        ...applyStepVisibiity(),
        grading_type: convertMakeReviewableToGradingType(),
        allow_resets: data.hasOwnProperty("allow_resets")
          ? data.allow_resets
            ? ALLOW_RESETS_ALWAYS
            : ALLOW_RESETS_BEFORE
          : ALLOW_RESETS_ALWAYS,
        lock_password: isLocked ? data.lock_password : null,
        points: parseInt(data.points) || 0,
        minimum_passing_score: parseInt(data.minimum_passing_score) || 0,
        time_to_complete: !isNaN(parseInt(data.time_to_complete))
          ? data.time_to_complete * 60
          : null,
        requires_api_completion: data.requires_api_completion
      }

      const values = this.filterValues(allValues)

      const result = await this.props.updatePathItem({
        ...this.props.pathItem,
        ...values
      })
      // legacy/client likely wants to know the item was updated
      this.props.onSubmit(result.data.updatePathItem)
    } catch (error) {
      this.handleError(error.message)
      return Promise.reject(error.message)
    }
  }

  render() {
    const { card, loading, pathItem, displayMoreSettings } = this.props

    return loading || !pathItem ? (
      <Card card={card}>
        <LoadingCircle />
      </Card>
    ) : (
      <FormikWithDiscardFormState
        initialValues={this.filterValues(this.initialValue)}
        validate={validate((key, value, values) => {
          switch (key) {
            case "time_to_complete":
              return validateIf(
                Boolean,
                customErrorMessage(
                  isInt,
                  isGreaterThanOrEqualTo(0),
                  "Please enter the number of minutes (e.g., 0, 15, 60,...)"
                )
              )(value, values)
            case "points":
              const againstValidator = values.minimum_passing_score
                ? againstField("minimum_passing_score", isGreaterThanOrEqualTo)
                : () => {}

              return findErrorMessage(
                isInt,
                isGreaterThanOrEqualTo(0),
                againstValidator
              )(value, values)
            case "minimum_passing_score":
              return findErrorMessage(
                isInt,
                isGreaterThanOrEqualTo(0),
                againstField("points", isLessThanOrEqualTo)
              )(value, values)
          }
        })}
      >
        {(form) => {
          const hasPoints = parseInt(form.values.points) > 0
          const isReviewable = form.values.grading_type === true

          return (
            <StyledCard card={card} title={pathItem ? pathItem.name : ""}>
              <form onSubmit={(e) => e.preventDefault()}>
                {!!this.state.error && (
                  <Alert
                    error={this.state.error}
                    onClear={() => this.handleError(null)}
                  />
                )}
                <FormStateSubmitButton
                  styleType="primary"
                  disabled={!form.isValid || !form.dirty}
                  onClick={() => this.onSubmit(form.values)}
                  label="Save"
                />
                {displayMoreSettings ? (
                  <Fieldset>
                    <CardBlock>
                      <SelectInput
                        name="step_visibility"
                        label="Step visibility"
                        layoutAsColumns
                        helperText="Who can see this step?"
                        tooltip={tooltipWithHelpArticleLink(
                          "Control who can access this step."
                        )}
                        simpleValue
                        collapse
                        options={[
                          {
                            value: REGISTERED_MEMBERS_ONLY,
                            label: "Only registered members"
                          },
                          {
                            value: PASSWORD_LOCKED,
                            label: "Registered with password"
                          },
                          {
                            value: ANYONE_CAN_PREVIEW,
                            label: "Anyone can preview"
                          }
                        ]}
                        onChange={(value) =>
                          form.setFieldValue("step_visibility", value)
                        }
                        value={form.values.step_visibility}
                      />
                    </CardBlock>
                    {form.values.step_visibility === PASSWORD_LOCKED ? (
                      <CardBlock>
                        <TextInput
                          name="lock_password"
                          label="Password"
                          helperText="Add a password that learners must enter to gain access to this step."
                          layoutAsColumns
                          placeholder="Enter a password"
                          required
                          onChange={(value, e) => form.handleChange(e)}
                          onBlur={(value, e) => form.handleBlur(e)}
                          value={form.values.lock_password}
                        />
                      </CardBlock>
                    ) : null}
                    {!this.hasLegacyContent && (
                      <CardBlock>
                        <ToggleInput
                          name="allow_resets"
                          label="Allow retries"
                          layoutAsColumns
                          helperText="Enable learners to reset this step after they complete it and try again."
                          tooltip={tooltipWithHelpArticleLink(
                            "When on, enables learners to reset and resubmit this step any number of times."
                          )}
                          onChange={(value, e) => form.handleChange(e)}
                          value={form.values.allow_resets}
                        />
                      </CardBlock>
                    )}
                    {!this.hasLegacyContent && (
                      <CardBlock>
                        <SelectInput
                          name="show_grading_feedback"
                          simpleValue
                          label="Show answers"
                          layoutAsColumns
                          helperText="When do learners see answers to assessment questions?"
                          tooltip={tooltipWithHelpArticleLink(
                            "Control when learners see correct answers and answer explanations for assessment questions."
                          )}
                          collapse
                          options={[
                            {
                              value: SHOW_GRADING_FEEDBACK_ALWAYS,
                              label: "After each question"
                            },
                            {
                              value: SHOW_GRADING_FEEDBACK_COMPLETION,
                              label: "After step is complete"
                            },
                            {
                              value: SHOW_GRADING_FEEDBACK_NEVER,
                              label: "Only show answers to staff"
                            }
                          ]}
                          onChange={(value) =>
                            form.setFieldValue("show_grading_feedback", value)
                          }
                          value={form.values.show_grading_feedback}
                        />
                      </CardBlock>
                    )}

                    {pathItem.completion_comment_required_status !== "D" && (
                      <CardBlock>
                        <RadioGroup
                          name="completion_comment_required_status"
                          label="Learner comment"
                          layoutAsColumns
                          tooltip={tooltipWithHelpArticleLink(
                            "Allows learners to leave a private comment for the teacher(s) when they complete this step."
                          )}
                          collapse
                          value={form.values.completion_comment_required_status}
                        >
                          {[
                            {
                              value: "D",
                              label: COMMENT_LABELS_MAP["D"]
                            },
                            {
                              value:
                                pathItem.completion_comment_required_status,
                              label:
                                COMMENT_LABELS_MAP[
                                  pathItem.completion_comment_required_status
                                ]
                            }
                          ].map((option, index) => {
                            return (
                              <RadioButton
                                name="completion_comment_required_status"
                                type="radio"
                                value={option.value}
                                onChange={form.handleChange}
                                checked={
                                  form.values
                                    .completion_comment_required_status ===
                                  option.value
                                }
                              >
                                <Text.Body>{option.label}</Text.Body>
                              </RadioButton>
                            )
                          })}
                        </RadioGroup>
                      </CardBlock>
                    )}
                    {!this.hasLegacyContent && (
                      <CardBlock>
                        <ToggleInput
                          name="requires_api_completion"
                          label="Require API completion"
                          onChange={(value, e) => form.handleChange(e)}
                          value={form.values.requires_api_completion}
                          helperText={
                            <>
                              Require an external Zapier or API call to complete
                              this step.
                              <br />
                              <div
                                style={{
                                  marginTop: "1em"
                                }}
                              >
                                <Copy text={pathItem.source_id}>
                                  <span
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      cursor: "pointer"
                                    }}
                                  >
                                    Step API ID: {pathItem.source_id}{" "}
                                    <Pathicon
                                      icon="copy"
                                      style={{ marginLeft: ".4em" }}
                                    />
                                  </span>
                                </Copy>
                              </div>
                            </>
                          }
                          tooltip={tooltipWithHelpArticleLink(
                            "Ensure an action is completed in an external system by making it only completable by a Zapier or API call.",
                            "https://help.pathwright.com/en/articles/5666026-validate-actions-taken-outside-of-pathwright-using-api-step-completion"
                          )}
                        />
                      </CardBlock>
                    )}
                  </Fieldset>
                ) : (
                  <Fieldset>
                    <CardBlock>
                      <TextInput
                        name="time_to_complete"
                        label="Estimated time to complete"
                        helperText="How long will this step likely take to complete?"
                        type="number"
                        layoutAsColumns
                        autoSize
                        custom={<StyledCustom>mins</StyledCustom>}
                        minWidth={30}
                        errors={getFormError(form, "time_to_complete")}
                        tooltip={tooltipWithHelpArticleLink(
                          "An estimate for learners to get a sense for how long this step may take to complete that rolls up to an estimate for the entire lesson."
                        )}
                        onChange={(value, e) => form.handleChange(e)}
                        onBlur={(value, e) => form.handleBlur(e)}
                        value={form.values.time_to_complete}
                      />
                    </CardBlock>
                    <CardBlock>
                      <ToggleInput
                        name="is_required"
                        label="Require completion"
                        layoutAsColumns
                        helperText="Ensure learners complete this step in order to complete the path."
                        tooltip={tooltipWithHelpArticleLink(
                          `When on, learners must complete this step in order to complete the ${this.props.resourceTypeLabel}. When off, they have the option to skip this step.`
                        )}
                        onChange={(value, e) => form.handleChange(e)}
                        value={form.values.is_required}
                      />
                    </CardBlock>
                    <CardBlock>
                      <ToggleInput
                        name="grading_type"
                        label="Make reviewable"
                        layoutAsColumns
                        helperText={`Notify staff to review each completion${
                          hasPoints
                            ? ` and award up to ${form.values.points} pts`
                            : ""
                        } when this step is completed.`}
                        tooltip={tooltipWithHelpArticleLink(
                          `Control whether staff are notified about completions and when and how points are awarded.`
                        )}
                        onChange={(value, e) => form.handleChange(e)}
                        value={form.values.grading_type}
                      />
                    </CardBlock>
                    <CardBlock>
                      <TextInput
                        name="points"
                        label="Award points"
                        layoutAsColumns
                        helperText={
                          isReviewable
                            ? `Staff ${
                                hasPoints ? "will" : "can optionally"
                              } award points when the step is reviewed.`
                            : `Points will be awarded automatically upon completion.`
                        }
                        autoSize
                        type="number"
                        placeholder="0"
                        custom={<StyledCustom>pts</StyledCustom>}
                        minWidth={30}
                        tooltip={tooltipWithHelpArticleLink(
                          `Award grade (or any kind of) points by adding any number > 0. Steps with points roll up to an overall score for the entire ${this.props.resourceTypeLabel}.`
                        )}
                        errors={getFormError(form, "points")}
                        onChange={(value, e) => {
                          form.handleChange(e)
                          // Automatically clear the `minimum_passing_score` if the `points` field has been reduce to 0.
                          // This provides better UX.
                          if (e.target.value == 0) {
                            form.setFieldValue("minimum_passing_score", 0, true)
                          }
                        }}
                        onBlur={(value, e) => form.handleBlur(e)}
                        value={form.values.points}
                      />
                    </CardBlock>

                    {hasPoints && (
                      <CardBlock>
                        <TextInput
                          name="minimum_passing_score"
                          label="Min. passing score"
                          helperText="Optionally require a minimum score to complete the step."
                          autoSize
                          layoutAsColumns
                          type="number"
                          placeholder="0"
                          custom={<StyledCustom>pts</StyledCustom>}
                          minWidth={30}
                          errors={getFormError(form, "minimum_passing_score")}
                          tooltip={tooltipWithHelpArticleLink(
                            "Add optional number of points learners must score in order to complete this step."
                          )}
                          onChange={(value, e) => form.handleChange(e)}
                          onBlur={(value, e) => form.handleBlur(e)}
                          value={form.values.minimum_passing_score}
                        />
                      </CardBlock>
                    )}
                    {/* Indicate unsaved changes in "More Settings" card.*/}
                    <FormikWithDiscardFormState
                      initialValues={this.filterValues(
                        this.initialValue,
                        true /* moreSettings */
                      )}
                      storageKey={getFormStateStorageKey(
                        path.join(window.location.pathname, `/more-settings/`)
                      )}
                    >
                      {({ discardCount }) => (
                        <CardLink
                          to={path.join(
                            window.location.pathname,
                            `/more-settings/`
                          )}
                          label="More Settings"
                          meta="Configure step visibility, retries, and when answers show up."
                          className="cursor-pointer CardLink"
                          // Indicating unsaved changes in "More Settings" card.
                          preview={
                            !!discardCount && (
                              <CountIndicator count={discardCount} />
                            )
                          }
                        />
                      )}
                    </FormikWithDiscardFormState>
                  </Fieldset>
                )}
              </form>
            </StyledCard>
          )
        }}
      </FormikWithDiscardFormState>
    )
  }
}

EditStepSettingsCard.displayName = "EditStepSettingsCard"

EditStepSettingsCard.propTypes = {
  source_id: PropTypes.number.isRequired,
  cohort_id: PropTypes.number.isRequired,
  path_id: PropTypes.number,
  onSubmit: PropTypes.func,
  displayMoreSettings: PropTypes.bool
}

EditStepSettingsCard.defaultProps = {
  onSubmit: () => null,
  source_id: null,
  cohort_id: null,
  path_id: null,
  displayMoreSettings: false
}

export default compose(
  graphql(GroupDocument, {
    options: ({ cohort_id }) => ({
      variables: {
        id: cohort_id
      }
    }),
    props: ({ data }) => ({
      error: data.error,
      loading: data.loading,
      cohort: data.group,
      resource: data.group ? data.group.resource : null // used by withTranslatedResourceTypeLabel
    })
  }),
  graphql(PathItemDocument, {
    options: ({ source_id, path_id, cohort_id }) => ({
      variables: {
        source_id,
        path_id,
        cohort_id
      }
    }),
    skip: ({ path_id, cohort_id }) => !path_id && !cohort_id,
    props: ({ data, ownProps }) => ({
      error: data.error || ownProps.error,
      loading: data.loading || ownProps.loading,
      pathItem: data.pathItem
    })
  }),
  withUpdatePathItem,
  withTranslatedResourceTypeLabel
)(EditStepSettingsCard)
